import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import className from 'classnames/bind'
import { MasonryGrid } from '@egjs/react-grid'
import dayjs from 'dayjs'
import { subDays, isValid, isWithinInterval } from 'date-fns'

import style from './MyhouseMainPage.module.scss'

import useUnitPrice from '@Hooks/myhouse/price/useUnitPrice'
import MyhouseMainHeader from '@Components/atoms/MyhouseMainHeader'
import MyhouseMainAssetTab from '@Components/atoms/MyhouseMainAssetTab'
import MyhouseMainRegisterDeriveGuide from '@Components/atoms/MyhouseMainRegisterDeriveGuide'
import MyhouseMainTradePrice from '@Components/myhouse/main/MyhouseMainTradePrice'
import MyhouseMainNoticePage from '@Components/templates/MyhouseMainNoticePage'
import useMain from '@Hooks/myhouse/main/useMain'
import { MyhouseLabelType } from '@Root/utils/houseLabelUtils'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import useMyhouse from '@Hooks/myhouse/useMyhouse'
import MyhousePricePage from '@Components/templates/MyhousePricePage'
import { HouseDetail } from '@Root/slices/myhouse/mainSlice'
import Footer from '@Components/common/Footer'
import useModal from '@Hooks/myhouse/useModal'
import useToast from '@Hooks/myhouse/useToast'
import useDebounce from '@Hooks/common/useDebounce'
import useSetting from '@Hooks/myhouse/setting/useSetting'
import usePrice from '@Hooks/myhouse/price/usePrice'
import { AUTH_STATUS, FETCH_STATUS, PROVIDER, TRADE } from '@Root/contants'
import MyhouseUnitTrendPrice from '@Components/atoms/MyhouseUnitTrendPrice'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import MyhouseUnitLandPrice from '@Components/atoms/MyhouseUnitLandPrice'
import usePtpArticle from '@Hooks/myhouse/usePtpArticle'
import MyhousePtpArticle from '@Components/modules/MyhousePtpArticle'
import MyhousePriceEmpty from '@Components/atoms/MyhousePriceEmpty'
import useBrowser from '@Hooks/myhouse/useBrowser'
import MyhouseMainResidentPage from '@Components/templates/MyhouseMainResidentPage'
import MyhouseMainMasonryContent from '@Components/atoms/MyhouseMainMasonryContent'
import MyhouseLoanRecommend from '@Components/atoms/MyhouseLoanRecommend'
import { getParameterByName } from '@Root/utils/routeUtils'
import { TOAST_TEXT } from '@Root/contants/myhouseToast'
import MyhouseMainCertifiedStateGuidePage from '@Components/modules/MyhouseMainCertifiedStateGuidePage'
import MyhouseCommonProgress from '@Components/atoms/MyhouseCommonProgress'
import MyhouseMainMasonryGridLayout from '@Components/atoms/MyhouseMainMasonryGridLayout'
import { NCLICK, NDS_PROTOCOL } from '@Root/contants/lcs'
import { lcsSend } from '@Helpers/lcs'
import { useSessionStorage } from '@Root/utils/storageUtils'
import MyhouseManagementFee from '@Components/modules/MyhouseManagementFee'
import MyhouseInsuranceGuide from '@Components/modules/MyhouseInsuranceGuide'
import { MY_HOUSE_AUTH_METHOD } from '@Root/contants/myhouse'
import {
  PROMOTION_PERIOD,
  LAST_PROMOTION_WEEK_INDEX,
  PROMOTION_CLOSE_POPUP_DATE,
  PROMOTION_END_DATE,
  PROMOTION_FAIL_GUIDE,
} from '@Root/contants/myhousePromotion'

const cx = className.bind(style)

const MyhouseMainPage: React.FC = () => {
  //변수선언부
  const { reduxMain, loadMainHouses, loadHouse, setRouteHouseNo, setCurrentHouseNo } = useMain()
  const { loadRemoveHouse, reduxSetting } = useSetting()
  const { loadMyHouseAuth } = useMyhouse()
  const { show } = useToast()
  const masonryGridRef = useRef<MasonryGrid>(null)

  const {
    currentHouseNo,
    routeHouseNo,
    house,
    tabHouseList,
    fetchStatus: houseFetchStatus,
    houseDetail,
    isAllCommercialRealEstate,
  } = reduxMain
  const { fetchStatusRemove } = reduxSetting

  const { houseNo } = useParams<{ houseNo: string }>()
  const { replace } = useHistory()
  const { pathname, search, hash } = useLocation()
  const { setComplexNo } = useMyhouse()
  const { clean, modal } = useModal()
  const { loadChart, loadTable, loadSummary, loadRealPriceTable, price, setTradeType, setPeriod } = usePrice()
  const { period, tradeType } = price

  const { unitPrice, setUnitTrendPrice, loadUnitTrendPrice, setUnitLandPrice, loadUnitLandPrice } = useUnitPrice()
  const { trendPrice, trendPriceDetail, landPrice, landPriceDetail } = unitPrice

  const [isShowLoading, setIsShowLoading] = useState(true)
  const { setStorage } = useSessionStorage()

  const {
    ptpArticle,
    loadSamePtpArticle,
    loadSimilarPtpArticle,
    setSamePtpArticle,
    setSimilarPtpArticle,
  } = usePtpArticle()
  const { sameArticleList, similarArticleList, sameArticleFetchStatus, similarArticleFetchStatus } = ptpArticle

  const { landDebounce } = useDebounce()
  const debouncedLoadRemoveHouse = landDebounce(loadRemoveHouse)

  const { open } = useModal()

  const complexNo = house?.complexNo || 0
  // TODO: 현재 등록된 인자값에 맞춘 내용으로 매우 거슬립니다.
  const areaNo = house?.pyeongNo || 0
  const tradeTypeCode = house?.tradeTypeCode || ''

  const { isMobile, isIpad } = useBrowser()

  const isOwnerAuthProgress = [AUTH_STATUS.APPLY1, AUTH_STATUS.APPLY2].includes(house?.ownerAuthStatus as AUTH_STATUS)
  const isResidentAuthProgress = [AUTH_STATUS.APPLY1, AUTH_STATUS.APPLY2].includes(
    house?.residentAuthStatus as AUTH_STATUS,
  )
  const isOwnerAuthCompletion = house?.ownerAuthStatus === AUTH_STATUS.COMPLETION
  const isResidentAuthCompletion = house?.residentAuthStatus === AUTH_STATUS.COMPLETION
  const isAuthCompletion = isOwnerAuthCompletion || isResidentAuthCompletion
  const isAuthProgress = !isAuthCompletion && (isOwnerAuthProgress || isResidentAuthProgress)

  const isOwnerAuthCompleteShown = house?.houseNo + '_ownerAuthToastMessage'
  const isResidentAuthCompleteShown = house?.houseNo + '_residentAuthToastMessage'

  const month = new Date().getMonth() + 1

  /*무료보험 프로모션 추가부분 시작(7.17 이후 제거)*/
  const today = dayjs().startOf('day')
  const lastPromotionStart = dayjs(PROMOTION_PERIOD[LAST_PROMOTION_WEEK_INDEX].start)
  const lastPromotionEnd = dayjs(PROMOTION_PERIOD[LAST_PROMOTION_WEEK_INDEX].end)
  const isLastPeriod = today.isSameOrAfter(lastPromotionStart) && today.isSameOrBefore(lastPromotionEnd)
  const isPromotionEnd = today.isAfter(dayjs(PROMOTION_END_DATE))

  /*프로모션 종료팝업은 23.07.17 이전까지 계속 노출*/
  const isInPromotionClosePopupDate = today.isBefore(dayjs(PROMOTION_CLOSE_POPUP_DATE))
  /*무료보험 프로모션 추가부분 끝*/

  //함수정의부
  const handleHouseClick = (event: React.SyntheticEvent, houseNo: number) => {
    event.preventDefault()
    if (reduxMain.fetchStatus !== FETCH_STATUS.PENDING) {
      setCurrentHouseNo({ currentHouseNo: houseNo })
      replace({
        pathname: `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/${houseNo}`,
      })
    }
  }

  const unitTrendPriceMoreInfoHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    if (isMobile || isIpad) {
      window.location.href = `${process.env.REACT_APP_SPACE_WEB_URL}/complex/landprice?hscpNo=${complexNo}&bildNo=${house?.buildingNo}&tabCategory=buildingTrendprice`
    } else {
      open(MODAL_TYPE.UNIT_TREND_PRICE_PC_GUIDE, {
        nclickCancel: NCLICK.MY_HOUSE.UNIT_TREND_PRICE.MOBILE_POPUP,
      })
    }
  }

  const myhouseAuthHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    window.location.href = `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${house?.houseNo}`
  }

  const unitLandPriceMoreInfoHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    if (isMobile || isIpad) {
      window.location.href = `${process.env.REACT_APP_SPACE_WEB_URL}/complex/landprice?hscpNo=${complexNo}&bildNo=${house?.buildingNo}`
    } else {
      window.location.href = `${process.env.REACT_APP_SPACE_SERVICE_URL}/complexes/${complexNo}?tab=buildingLandprice`
    }
  }

  const ptpArticleMoreInfoHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    window.location.href = getMorePtpArticleUrl()
  }

  const getMorePtpArticleUrl = () => {
    let url
    if (house?.complexNo) {
      if (house?.pyeongNo) {
        //동일면적 매물 더보기
        url = `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${complexNo}?tradTpCd=${tradeTypeCode}&ptpNo=${house.pyeongNo}&bildNo=&articleListYN=Y`
      } else {
        // 단지형 유사면적매물
        let realestateCategoryCodes
        if (house.realestateTypeCode.startsWith('B')) {
          realestateCategoryCodes = 'ABYG:OBYG'
        } else {
          realestateCategoryCodes = house.realestateCategoryCode
        }

        const pyeongNameParam = getPyeongNameParam(house?.pyeongName)
        url = `${process.env.REACT_APP_SPACE_WEB_URL}/map/0:0:14:${house?.cortarNo}/${realestateCategoryCodes}/${tradeTypeCode}?${pyeongNameParam}`
      }
    } else {
      let realestateCategoryCodes
      let pyeongNameParam

      if (house?.realestateCategoryCode && ['APT', 'OPST'].includes(house.realestateCategoryCode)) {
        // 비단지형 아파트, 오피스텔 유사면적매물
        realestateCategoryCodes = house.realestateCategoryCode
      } else {
        // 비단지형 주택 유사면적매물
        realestateCategoryCodes = 'VL:JWJT:DDDGG:SGJT:HOJT'
      }
      if (house?.pyeongName) {
        pyeongNameParam = getPyeongNameParam(house.pyeongName)
      }

      url = `${process.env.REACT_APP_SPACE_WEB_URL}/map/0:0:14:${house?.cortarNo}/${realestateCategoryCodes}/${tradeTypeCode}?${pyeongNameParam}`
    }

    return url
  }

  const getPyeongNameParam = (pyeongName: string) => {
    let param = ''
    const pyeong = parseInt(pyeongName, 10)

    if (!isNaN(pyeong) && pyeong > 0) {
      const min = Math.floor(pyeong * 0.9)
      const max = Math.floor(pyeong * 1.1)
      param += `spcMin=${min}&spcMax=${max}`
    }

    return param
  }

  const articleMoreHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    let realestateCategoryCode = ''
    if (house?.realestateCategoryCode && ['APT', 'OPST', 'SG', 'GM'].includes(house.realestateCategoryCode)) {
      realestateCategoryCode = house.realestateCategoryCode
    } else {
      realestateCategoryCode = 'VL:JWJT:DDDGG:SGJT:HOJT'
    }
    window.location.href = `${process.env.REACT_APP_SPACE_WEB_URL}/map/0:0:14:${house?.cortarNo}/${realestateCategoryCode}/${tradeTypeCode}`
  }

  //useEffect
  useEffect(() => {
    lcsSend(NDS_PROTOCOL.MY_HOUSE.HOME.MAIN)
    if (houseNo) {
      setRouteHouseNo(Number(houseNo))
      const popup = getParameterByName('popup')
      const modalType: MODAL_TYPE = MODAL_TYPE[popup as keyof typeof MODAL_TYPE]
      if (popup && modalType) {
        open(modalType)
      }
      const toast = getParameterByName('toast')
      const toastText: TOAST_TEXT = TOAST_TEXT[toast as keyof typeof TOAST_TEXT]
      if (toast && toastText) {
        show(toastText)
      }

      // remove queryString
      if (search) {
        replace({
          pathname: pathname,
          hash: hash,
        })
      }
    } // url 에 houseNo 가 있는 경우 집 리스트 불러왔을때 이 값을 선택한 집으로 설정 합니다.
    loadMainHouses()
    loadMyHouseAuth()

    /*무료보험 프로모션 추가부분 시작(7.17 이후 제거)*/
    if (isPromotionEnd && isInPromotionClosePopupDate) {
      if (!localStorage.getItem('myhouse:promotion:end:close')) {
        open(MODAL_TYPE['우리집 프로모션 종료 시'])
      }
    }
    /*무료보험 프로모션 추가부분 끝*/
  }, []) // 최초 로딩시 우리집 목록을 불러온다.

  useEffect(() => {
    if (currentHouseNo >= 0) {
      loadHouse(currentHouseNo)
    } else if (currentHouseNo === -1) {
      replace(`${LAND_SERVICE_PREFIX_URL.MYHOUSE}/regist/guide`)
    }
  }, [currentHouseNo])

  useEffect(() => {
    if (houseFetchStatus !== FETCH_STATUS.SUCCESS) {
      return
    }

    if (routeHouseNo !== currentHouseNo) {
      // :houseNo로 의도한 우리집이 아니라 다른 우리집을 보여줄 때. hash 제거
      // 실거래 알림랜딩시 제거된 우리집번호로 랜딩할 때 첫 번째 우리집의 시세,실거래가 영역으로 가는 것 방지
      replace(`${LAND_SERVICE_PREFIX_URL.MYHOUSE}/${currentHouseNo}`)
    }

    // 공지사항, 리뷰리스트 뒤로가기 complexNo -> houseNo 갱신
    setStorage(['bbs', 'complex', 'house', complexNo], currentHouseNo)

    if (!house?.complexNo || !(house?.buildingNo > 0) || !(house?.hoNo > 0)) {
      setUnitTrendPrice()
      setUnitLandPrice()
      return
    }

    if ((house?.ownerAuth === 'Y' || house?.residentAuth === 'Y') && house?.isTrendPriceExist) {
      loadUnitTrendPrice(house.houseNo)
    } else {
      setUnitTrendPrice()
    }

    loadUnitLandPrice({
      complexNo: house?.complexNo ? house.complexNo.toString() : '',
      bildNo: house?.buildingNo ? house.buildingNo.toString() : '',
      hoNo: house?.hoName ? house.hoName : '',
    })
  }, [houseFetchStatus])

  useEffect(() => {
    setComplexNo(complexNo)
    setTradeType(tradeTypeCode as TRADE) // 확인 필요
  }, [complexNo, tradeTypeCode])

  useEffect(() => {
    if (modal.confirmModal === MODAL_TYPE.DELETE_MYHOUSE) {
      clean()
      debouncedLoadRemoveHouse({ houseNo: currentHouseNo })
    }
  }, [modal.confirmModal])

  useEffect(() => {
    if (fetchStatusRemove === FETCH_STATUS.SUCCESS) {
      show('우리집이 삭제되었습니다.')
      setTimeout(() => {
        window.location.href = `${LAND_SERVICE_PREFIX_URL.MYHOUSE}`
      }, 1500)
    }
  }, [fetchStatusRemove])

  useEffect(() => {
    if (complexNo !== 0 && areaNo && tradeType) {
      if ([TRADE.DEAL, TRADE.LEASE].includes(tradeType as TRADE)) {
        loadChart({ complexNo, areaNo, tradeType: tradeType as TRADE, year: period, priceChartChange: false })
        loadSummary({ complexNo, areaNo, tradeType: tradeType as TRADE, year: period, priceChartChange: false })
      } else {
        loadSummary({
          complexNo,
          areaNo,
          tradeType: tradeType as TRADE,
          year: period,
          provider: PROVIDER.KBSTAR,
          priceChartChange: false,
        })
      }
      loadTable({ complexNo, areaNo, tradeType: tradeType as TRADE, year: period, priceChartChange: false })
      loadRealPriceTable({ complexNo, areaNo, tradeType: tradeType as TRADE, year: period, priceChartChange: false })
    }
  }, [tradeType, period, areaNo])

  useEffect(() => {
    if (house?.realestateTypeCode) {
      if (
        ['A01', 'A02', 'A03', 'A04', 'B01', 'B02', 'C02', 'C03', 'D05'].includes(house.realestateTypeCode) &&
        !house?.pyeongNo &&
        house?.pyeongName
      ) {
        loadSimilarPtpArticle({
          complexNo: house?.complexNo !== undefined ? house?.complexNo?.toString() : '0',
          cortarNo: house?.cortarNo.toString(),
          pyeong: house?.pyeongName,
          realestateTypeCode: house?.realestateTypeCode,
          tradeType: house?.tradeTypeCode,
          order: 'spcAscDateDesc',
        })
        setSamePtpArticle()
      } else if (
        ['A01', 'A02', 'A03', 'A04', 'B01', 'B02'].includes(house.realestateTypeCode) &&
        house?.complexNo &&
        house?.pyeongNo
      ) {
        loadSamePtpArticle({
          complexNo: house?.complexNo?.toString() || null,
          pyeongNo: house?.pyeongNo,
          tradeType: house?.tradeTypeCode,
          order: 'dateDesc',
        })
        setSimilarPtpArticle()
      } else {
        setSamePtpArticle()
        setSimilarPtpArticle()
      }
    }

    if (house?.complexNo) {
      if (isOwnerAuthCompletion) {
        if (!localStorage.getItem(isOwnerAuthCompleteShown)) {
          localStorage.setItem(isOwnerAuthCompleteShown, 'Y')
          open(MODAL_TYPE.AUTH_COMPLETE_OWNER, { complexNo: house.complexNo })
        }
      }

      if (isResidentAuthCompletion) {
        if (
          house?.authMethod === MY_HOUSE_AUTH_METHOD.MANAGEMENT_FEE ||
          house?.authMethod === MY_HOUSE_AUTH_METHOD.PACKAGE
        ) {
          if (!localStorage.getItem(isResidentAuthCompleteShown)) {
            localStorage.setItem(isResidentAuthCompleteShown, 'Y')
            open(MODAL_TYPE.AUTH_COMPLETE_RESIDENT, { complexNo: house.complexNo })
          }
        }
      }
    }
  }, [house?.houseNo])

  useEffect(() => {
    if (houseFetchStatus === FETCH_STATUS.SUCCESS) {
      setIsShowLoading(false)
    }
  }, [houseFetchStatus])

  const isShowExpirationInfo = useMemo(() => {
    if (!house?.tradeEndDate || !tradeTypeCode) {
      return false
    }

    const today = new Date()
    const tradeEndDate = new Date(house?.tradeEndDate)
    const threeMonthBeforeOfTradeEndDate = subDays(tradeEndDate, 90)

    /**
     * 날짜 비교만 하기 위해, 시간을 모두 자정으로 설정
     */
    today.setHours(0, 0, 0, 0)
    tradeEndDate.setHours(0, 0, 0, 0)
    threeMonthBeforeOfTradeEndDate.setHours(0, 0, 0, 0)

    return (
      [TRADE.LEASE, TRADE.RENT].includes(tradeTypeCode as TRADE) &&
      isValid(tradeEndDate) &&
      isWithinInterval(today, { start: threeMonthBeforeOfTradeEndDate, end: tradeEndDate })
    )
  }, [house?.tradeEndDate, tradeTypeCode])

  return (
    <div className={cx('main', isShowLoading && 'loading')}>
      <MyhouseMainHeader alarm={currentHouseNo >= 0} setting={currentHouseNo >= 0} currentHouseNo={currentHouseNo} />
      {tabHouseList !== null && currentHouseNo >= 0 && (
        <>
          <MyhouseMainAssetTab
            houses={tabHouseList as MyhouseLabelType[]}
            currentHouseNo={currentHouseNo}
            handleHouseClick={handleHouseClick}
          />
          {isShowLoading && <MyhouseCommonProgress className={cx('progress')} />}
          <MyhouseMainMasonryGridLayout ref={masonryGridRef} show={!isShowLoading}>
            <MyhouseMainMasonryContent>
              <MyhouseMainTradePrice
                houseDetail={houseDetail as HouseDetail}
                tradeTypeCode={tradeTypeCode}
                isRegisteredDongHo={house?.isRegisteredDongHo}
              />
              {isShowExpirationInfo && (
                <MyhouseLoanRecommend
                  houseNo={houseNo}
                  tradeTypeCode={house?.tradeTypeCode as TRADE.LEASE | TRADE.RENT}
                />
              )}
            </MyhouseMainMasonryContent>
            <MyhouseMainMasonryContent>
              <MyhouseMainCertifiedStateGuidePage
                houseDetail={houseDetail as HouseDetail}
                tradeTypeCode={tradeTypeCode}
                realestateTypeCode={house?.realestateTypeCode}
                authMethod={house?.authMethod}
              />
              {/*무료보험 프로모션 추가부분 시작*/}
              {tradeType === TRADE.DEAL && (
                <MyhouseInsuranceGuide
                  text={
                    houseDetail?.ownerAuthStatus === AUTH_STATUS.REJECT
                      ? PROMOTION_FAIL_GUIDE.REJECT
                      : PROMOTION_FAIL_GUIDE.FAIL
                  }
                  houseNo={house?.houseNo}
                  ownerAuthStatus={houseDetail?.ownerAuthStatus as AUTH_STATUS}
                  authMethod={house?.authMethod}
                />
              )}
              {/*무료보험 프로모션 추가부분 끝*/}
              {isAllCommercialRealEstate && (
                <MyhouseMainRegisterDeriveGuide houses={tabHouseList as MyhouseLabelType[]} />
              )}
              <MyhouseMainNoticePage />
            </MyhouseMainMasonryContent>
            <MyhouseMainMasonryContent>
              {house?.realestateTypeCode &&
                ['A01', 'A02', 'A03', 'A04', 'B01', 'B02', 'B03'].includes(house.realestateTypeCode) &&
                house?.complexNo &&
                house?.pyeongName &&
                !similarArticleList && (
                  <MyhousePricePage
                    price={price}
                    setTradeType={setTradeType}
                    setPeriod={setPeriod}
                    tradeTypeCurrentTab={tradeType}
                  />
                )}
              {house?.isTrendPriceExist &&
                tradeType === TRADE.DEAL &&
                !(house?.ownerAuthStatus === AUTH_STATUS.FAIL && house?.residentAuthStatus === AUTH_STATUS.FAIL) && (
                  <MyhouseUnitTrendPrice
                    detailAddress={houseDetail?.detailAddress}
                    ownerAuth={houseDetail?.ownerAuth}
                    residentAuth={houseDetail?.residentAuth}
                    buildingNo={house?.buildingNo}
                    hoNo={house?.hoNo}
                    isTrendPriceExist={house?.isTrendPriceExist}
                    isRegisteredDongHo={house?.isRegisteredDongHo}
                    houseNo={house?.houseNo}
                    isAuthProgress={isAuthProgress}
                    unitTrendPriceMoreInfoHandler={unitTrendPriceMoreInfoHandler}
                    myhouseAuthHandler={myhouseAuthHandler}
                    {...trendPriceDetail}
                  />
                )}
              {landPrice && tradeType === TRADE.DEAL && (
                <MyhouseUnitLandPrice
                  detailAddress={houseDetail?.detailAddress}
                  unitLandPriceMoreInfoHandler={unitLandPriceMoreInfoHandler}
                  isTrendPriceAreaExist={
                    house?.isTrendPriceExist &&
                    !(house?.ownerAuthStatus === AUTH_STATUS.FAIL && house?.residentAuthStatus === AUTH_STATUS.FAIL)
                  }
                  {...landPriceDetail}
                />
              )}
              {sameArticleList && sameArticleList?.totalCount > 0 && (
                <MyhousePtpArticle
                  articleList={sameArticleList?.articleList}
                  totalCount={sameArticleList?.totalCount}
                  ptpArticleMoreInfoHandler={ptpArticleMoreInfoHandler}
                  isSamePtpArticle
                  inputStatus
                />
              )}
              {similarArticleList && similarArticleList?.totalCount > 0 && (
                <MyhousePtpArticle
                  articleList={similarArticleList?.articleList}
                  totalCount={similarArticleList?.totalCount}
                  ptpArticleMoreInfoHandler={ptpArticleMoreInfoHandler}
                  isSimilarPtpArticle
                  inputStatus
                />
              )}
              {/*노출할 수 있는 시세정보가 없고, 설정한 면적정보가 없고, '아파트','오피스텔','빌라/연립','단독/다가구','상가주택','분양권'인 경우*/}
              {house?.realestateTypeCode &&
                (!sameArticleList || sameArticleList?.totalCount === 0) &&
                (!similarArticleList || similarArticleList?.totalCount === 0) &&
                !trendPrice &&
                !landPrice &&
                !house?.pyeongName &&
                !house?.pyeongNo &&
                ['A01', 'A02', 'C02', 'C03', 'D05', 'B01', 'B02'].includes(house.realestateTypeCode) && (
                  <MyhousePtpArticle houseNo={house?.houseNo} />
                )}
              {/* 1.'상가' 또는 '건물'이거나
                  2. '아파트','오피스텔','빌라/연립','단독/다가구','상가주택'인데 노출할 수 있는 시세정보가 없고, DB에 단지가 없고, 설정한 평형/면적 정보가 있는 경우 */}
              {house?.realestateTypeCode &&
                (['D02', 'D03'].includes(house.realestateTypeCode) ||
                  (['A01', 'A02', 'C02', 'C03', 'D05'].includes(house.realestateTypeCode) &&
                    !house?.complexNo &&
                    ((sameArticleFetchStatus === FETCH_STATUS.SUCCESS && sameArticleList?.totalCount === 0) ||
                      (similarArticleFetchStatus === FETCH_STATUS.SUCCESS && similarArticleList?.totalCount === 0)) &&
                    !trendPrice &&
                    !landPrice &&
                    (house?.pyeongNo || house?.pyeongName))) && (
                  <MyhousePriceEmpty articleMoreHandler={articleMoreHandler} />
                )}
            </MyhouseMainMasonryContent>
            <MyhouseMainMasonryContent>
              <MyhouseMainResidentPage />
              <MyhouseManagementFee />
            </MyhouseMainMasonryContent>
          </MyhouseMainMasonryGridLayout>
          <Footer show={!isShowLoading} />
        </>
      )}
    </div>
  )
}

export default MyhouseMainPage
